// The airframe's Schedule tab (F520, decision 128): the same calendar for
// one aircraft, a lane per day, the next days first; the free half hours
// clickable, the aircraft booked for you when your slot opens.

import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { queries, type Slot } from "@/api";
import { ErrorNote } from "@/components/ErrorNote";
import { ScheduleBoard, SlotCard, SlotHistory, SlotPopover, useTicking, type Lane } from "@/components/ScheduleBoard";
import { addMinutes, dayStartUtc, localClockOf, utcClock } from "@/lib/schedule";

export const Route = createFileRoute("/airframes/$id/schedule")({
  component: AirframeSchedule,
});

function AirframeSchedule() {
  const { id } = Route.useParams();
  const me = useQuery(queries.me());
  const airframe = useQuery(queries.airframe(id));
  const now = useTicking();
  const [days, setDays] = useState(3);
  const from = dayStartUtc(now);
  const to = addMinutes(from, days * 24 * 60);
  const slots = useQuery(queries.airframeSlots(id, from.toISOString(), to.toISOString()));
  const [booking, setBooking] = useState<{ lane: Lane; startsAt: Date } | null>(null);
  const [picked, setPicked] = useState<{ slot: Slot; lane: Lane } | null>(null);
  if (me.isPending || airframe.isPending) return null;
  if (!me.data || !airframe.data) return <p className="muted">Sign in to see the schedule.</p>;
  const a = airframe.data;
  const meId = me.data.user.id;
  const manages = a.your_role === "owner" || a.your_role === "captain";
  const canBook = manages || a.your_role === "pilot";
  const lane: Lane = {
    id: a.id,
    reg: a.identity.reg,
    name: a.name ?? "",
    manages,
    canBook,
    booking: a.lease ? { holder: { id: a.lease.holder.id, name: a.lease.holder.name, handle: a.lease.holder.handle }, acquired_at: a.lease.acquired_at, flying: !!a.lease.flying } : null,
    slots: slots.data?.items ?? [],
  };
  return (
    <div className="space-y-4" data-testid="airframe-schedule">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h2 className="text-[1.0714rem] font-semibold">Schedule</h2>
        <p className="muted text-sm">Every time is UTC. Click a free half hour to book it: {a.identity.reg} is yours when it opens.</p>
        <span className="ml-auto tnum text-sm">
          {utcClock(now)} <span className="muted text-xs">UTC · {localClockOf(now)} on your clock</span>
        </span>
      </div>
      <ErrorNote error={slots.error} />
      {slots.data ? (
        <div className="space-y-3">
          {Array.from({ length: days }, (_, i) => {
            const dayStart = addMinutes(from, i * 24 * 60);
            const dayEnd = addMinutes(dayStart, 24 * 60);
            const dayLane: Lane = {
              ...lane,
              // The live booking belongs to today's lane alone.
              booking: i === 0 ? lane.booking : null,
              slots: lane.slots.filter((s) => new Date(s.ends_at) > dayStart && new Date(s.starts_at) < dayEnd),
            };
            return (
              <ScheduleBoard
                key={dayStart.toISOString()}
                lanes={[dayLane]}
                dayStart={dayStart}
                now={now}
                meId={meId}
                picked={booking ? { laneId: booking.lane.id, startsAt: booking.startsAt } : null}
                compact
                onBook={(l, startsAt) => {
                  setPicked(null);
                  setBooking({ lane: l, startsAt });
                }}
                onPick={(slot, l) => {
                  setBooking(null);
                  setPicked({ slot, lane: l });
                }}
              />
            );
          })}
          {days < 14 ? (
            <button type="button" className="btn btn-sm" onClick={() => setDays(14)} data-testid="schedule-two-weeks">
              Show the two weeks
            </button>
          ) : null}
        </div>
      ) : null}
      {booking ? <SlotPopover lane={booking.lane} startsAt={booking.startsAt} meId={meId} onDone={() => setBooking(null)} onClose={() => setBooking(null)} /> : null}
      {picked ? <SlotCard slot={picked.slot} lane={picked.lane} now={now} onClose={() => setPicked(null)} /> : null}
      <SlotHistory items={lane.slots.map((slot) => ({ slot, lane }))} meId={meId} showDay />
    </div>
  );
}
